/**
 * Enum of possible Modbus error codes
 * and their meanings.
 */
export enum ModbusErrorCode {
  /** Modbus standard exception codes — do not collide with 201+ */
  IllegalFunction                = 0x01,  // 1
  IllegalDataAddress             = 0x02,  // 2
  IllegalDataValue               = 0x03,  // 3
  SlaveDeviceFailure             = 0x04,  // 4
  SlaveDeviceBusy                = 0x06,  // 6  — device is processing, retry later
  GatewayPathUnavailable         = 0x0A,  // 10 — device unreachable behind gateway
  GatewayTargetFailed            = 0x0B,  // 11 — gateway cannot reach target device

  NotEnoughBytesFunctionCode      = 201,
  NotEnoughBytesExceptionResponse = 202,
  NotEnoughBytesByteCount        = 203,
  NotEnoughBytesReadResponse     = 204,
  NotEnoughBytesWriteResponse    = 205,
  UnsupportedFunctionCode        = 206,
  ExtraBytesAfterDecoding        = 207,

  BusError0500                   = 210,

  Timeout                        = 220,
  RequestFailed                  = 221,

  WriteVerificationFailed        = 230,

  FatalError                     = 250,

  ModbusError                   = 255,
}

/**
 * A custom ModbusError that includes a `code` number
 * to uniquely identify the error type.
 */
export class ModbusError extends Error {
  readonly exceptionCode?: number;
  readonly subResponseIndex?: number;
  readonly registerAddress?: number;
  readonly expectedValue?: number;
  readonly actualValue?: number;

  constructor(
    message: string,
    public code: ModbusErrorCode,
    options?: {
      exceptionCode?: number;
      subResponseIndex?: number;
      registerAddress?: number;
      expectedValue?: number;
      actualValue?: number;
    },
  ) {
    super(message);
    this.exceptionCode = options?.exceptionCode;
    this.subResponseIndex = options?.subResponseIndex;
    this.registerAddress = options?.registerAddress;
    this.expectedValue = options?.expectedValue;
    this.actualValue = options?.actualValue;
  }

  static async request(req: (cmd: Uint8Array) => Promise<Uint8Array>, input: Uint8Array): Promise<Uint8Array> {
    try {
      return await req(input);
    } catch (_) {
      throw new ModbusError('Request failed', ModbusErrorCode.RequestFailed);
    }
  }

  static timeout(ms: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new ModbusError('Timeout', ModbusErrorCode.Timeout)), ms);
    });
  }
}

/** Map standard Modbus exception codes to specific error codes.
 *  Unmapped codes fall back to ModbusErrorCode.ModbusError (255). */
export const MODBUS_EXCEPTION_CODE_MAP: Record<number, ModbusErrorCode> = {
  0x01: ModbusErrorCode.IllegalFunction,
  0x02: ModbusErrorCode.IllegalDataAddress,
  0x03: ModbusErrorCode.IllegalDataValue,
  0x04: ModbusErrorCode.SlaveDeviceFailure,
  0x06: ModbusErrorCode.SlaveDeviceBusy,
  0x0A: ModbusErrorCode.GatewayPathUnavailable,
  0x0B: ModbusErrorCode.GatewayTargetFailed,
};

/**
 * Concatenate multiple sub-requests into a single Uint8Array.
 */
export const encodeMultiQuery = (requests: Uint8Array[]): Uint8Array => {
  const concatenated = requests.reduce(
    (acc, cur) => [...acc, ...Array.from(cur)],
    [] as number[],
  );
  return new Uint8Array(concatenated);
};

/**
 * Decode a "multi-response" (concatenated sub-responses) into
 * an array of sub-responses `Uint8Array`.
 */
const DECODE_DEBUG = true;
const dLog = (msg: string, data?: unknown) => DECODE_DEBUG && console.log(`[decodeMulti] ${msg}`, data !== undefined && data !== null && typeof data === 'object' ? JSON.stringify(data) : (data ?? ''));

export const decodeMultiQuery = (
  multiResponse: Uint8Array,
  numberOfSubRequests: number
): Uint8Array[] => {
  const rawData = Array.from(multiResponse);
  const subResponses: Uint8Array[] = [];
  dLog(`start: ${multiResponse.length}B total, expecting ${numberOfSubRequests} sub-response(s)`);

  for (let i = 0; i < numberOfSubRequests; i++) {
    if (rawData.length < 2) {
      throw new ModbusError(
        `Not enough bytes to read function code for sub-response #${i}`,
        ModbusErrorCode.NotEnoughBytesFunctionCode
      );
    }

    const deviceAddress = rawData[0];
    const functionCode = rawData[1];

    /** functionCode >= 0x80 => Exception response */
    if (functionCode >= 0x80) {
      const exceptionCode = rawData.length >= 3 ? rawData[2] : undefined;

      const mappedCode = exceptionCode !== undefined
        ? (MODBUS_EXCEPTION_CODE_MAP[exceptionCode] ?? ModbusErrorCode.ModbusError)
        : ModbusErrorCode.ModbusError;

      const mappedName = exceptionCode !== undefined
        ? (ModbusErrorCode[mappedCode] ?? 'Unknown')
        : 'Unknown';

      dLog(`Exception on sub-response #${i}: FC=0x${functionCode.toString(16)}, exceptionCode=0x${(exceptionCode ?? 0).toString(16)} -> ${mappedName}`);

      /** Consume the 5-byte exception response (addr + FC|0x80 + exCode + CRC_lo + CRC_hi) */
      const exceptionFrameLen = Math.min(5, rawData.length);
      rawData.splice(0, exceptionFrameLen);

      throw new ModbusError(
        `Detected exception response 0x${functionCode.toString(16)} (exceptionCode=0x${(exceptionCode ?? 0).toString(16)}) on sub-response #${i}`,
        mappedCode,
        { exceptionCode, subResponseIndex: i },
      );
    }

    /** 0x0500 => BusError0500 */
    if (deviceAddress === 0x05 && functionCode === 0x00) {
      throw new ModbusError(
        `Detected bus error 0x0500 on sub-response #${i}`,
        ModbusErrorCode.BusError0500
      );
    }

    switch (functionCode) {
      case 0x04:
      case 0x03: {
        if (rawData.length < 3) {
          throw new ModbusError(
            `Not enough bytes to read byteCount for sub-response #${i}`,
            ModbusErrorCode.NotEnoughBytesByteCount
          );
        }
        const byteCount = rawData[2];
        const totalLen = 3 + byteCount + 2;
        if (rawData.length < totalLen) {
          throw new ModbusError(
            `Not enough bytes for read sub-response #${i}, expected ${totalLen}, got ${rawData.length}`,
            ModbusErrorCode.NotEnoughBytesReadResponse
          );
        }

        const readResponse = new Uint8Array(rawData.splice(0, totalLen));
        dLog(`  [${i}] READ FC=0x0${functionCode.toString(16)}: addr=0x${deviceAddress.toString(16)}, byteCount=${byteCount}, frame=${totalLen}B, remaining=${rawData.length}B`);
        subResponses.push(readResponse);
        break;
      }

      case 0x10: {
        const totalLen = 8;
        if (rawData.length < totalLen) {
          throw new ModbusError(
            `Not enough bytes for write sub-response (0x10) #${i}, expected ${totalLen}, got ${rawData.length}`,
            ModbusErrorCode.NotEnoughBytesWriteResponse
          );
        }
        const writeResponse = new Uint8Array(rawData.splice(0, totalLen));
        const startAddr = (writeResponse[2] << 8) | writeResponse[3];
        const qty = (writeResponse[4] << 8) | writeResponse[5];
        dLog(`  [${i}] WRITE FC=0x10: addr=0x${startAddr.toString(16).toUpperCase().padStart(4, '0')}, qty=${qty}, frame=${totalLen}B, remaining=${rawData.length}B`);
        subResponses.push(writeResponse);
        break;
      }

      default:
        throw new ModbusError(
          `Unsupported function code 0x${functionCode.toString(16)} on sub-response #${i}`,
          ModbusErrorCode.UnsupportedFunctionCode
        );
    }
  }

  if (rawData.length > 0) {
    throw new ModbusError(
      `There are ${rawData.length} extra bytes after decoding all sub-responses`,
      ModbusErrorCode.ExtraBytesAfterDecoding
    );
  }

  return subResponses;
};

